<template>
  <!-- Secondary nav that docks against the main sidebar and shows on the Tasks route. -->
  <aside class="project-nav" :dir="isRtl ? 'rtl' : 'ltr'">
    <div class="pn-head">{{ t('nav.projects') }}</div>

    <nav class="pn-list">
      <button class="pn-item" :class="{ active: !activeProject }" @click="go('')">
        <span class="pn-name">{{ t('tasks.allProjects') }}</span>
      </button>
      <button
        v-for="p in projects"
        :key="p.id"
        class="pn-item"
        :class="{ active: activeProject === String(p.id) }"
        @click="go(p.id)"
      >
        <span class="pn-name">{{ p.name }}</span>
        <span class="pn-count">{{ p.task_count }}</span>
        <span class="pn-tick" :style="{ background: p.color }"></span>
      </button>
    </nav>

    <router-link to="/projects" class="pn-new">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
      {{ t('projects.newProject') }}
    </router-link>
  </aside>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { onRealtime, debounce } from '@/composables/useRealtime'

export default {
  name: 'ProjectNav',
  setup() {
    const route  = useRoute()
    const router = useRouter()
    const { t, isRtl } = useI18n()
    const projects = ref([])

    const activeProject = computed(() => (route.query.project_id ? String(route.query.project_id) : ''))

    async function load() {
      try { projects.value = (await api.getProjects()).data || [] } catch {}
    }

    // Navigate by URL so TasksView reacts via its existing route watcher; preserve
    // the My/Team (mine) scope so picking a project doesn't switch boards.
    function go(id) {
      const query = {}
      if (route.query.mine) query.mine = route.query.mine
      if (id) query.project_id = id
      router.push({ name: 'tasks', query })
    }

    let offTask = () => {}, offProj = () => {}
    onMounted(() => {
      load()
      const r = debounce(load, 400)
      offTask = onRealtime('task.changed', r)
      offProj = onRealtime('project.changed', r)
    })
    onUnmounted(() => { offTask(); offProj() })

    return { t, isRtl, projects, activeProject, go }
  },
}
</script>

<style scoped>
/* Floating panel docked beside the main sidebar (which is pinned physical-left). */
.project-nav {
  width: 226px; flex-shrink: 0;
  margin: 16px 0 16px -6px;
  position: sticky; top: 16px;
  height: calc(100vh - 32px);
  background: var(--surface);
  border: 1.5px solid var(--card-brd);
  border-radius: 20px;
  box-shadow: 0 10px 28px rgba(16,22,40,.10);
  display: flex; flex-direction: column;
  padding: 16px 12px;
  animation: pn-in .2s ease;
}
@keyframes pn-in { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: none; } }

.pn-head {
  font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  color: var(--muted); padding: 2px 10px 12px;
}

.pn-list { flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 2px; }
.pn-item {
  display: flex; align-items: center; gap: 8px;
  width: 100%; text-align: start;
  padding: 9px 10px; border: none; background: none; cursor: pointer;
  border-radius: 10px; font-size: 13.5px; color: var(--text);
  transition: background .12s, color .12s;
}
.pn-item:hover { background: var(--hover); }
.pn-item.active { background: var(--accent-wash); color: var(--accent-ink); font-weight: 700; }
.pn-name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pn-count { font-family: var(--font-mono); font-size: 10.5px; color: var(--muted); }
.pn-item.active .pn-count { color: var(--accent-ink); }
.pn-tick { width: 4px; height: 18px; border-radius: 3px; flex-shrink: 0; background: var(--border-2); }

.pn-new {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  margin-top: 10px; padding: 9px 10px;
  border-radius: 10px; border: 1px dashed color-mix(in srgb, var(--accent-ink) 36%, transparent);
  color: var(--accent-ink); font-size: 13px; font-weight: 600; text-decoration: none;
}
.pn-new:hover { background: var(--accent-wash); }

/* On mobile the main sidebar is an off-canvas drawer; hide this secondary panel. */
@media (max-width: 768px) {
  .project-nav { display: none; }
}
</style>
